/**
 * Tagger-specific admin handler
 * 
 * Extends base entity admin with Tag-specific functionality:
 * - Tag assignments
 * - Search with filters
 * - Namespace filtering
 * - Type filtering
 */

import { useState, useCallback, useEffect } from "react";
import type { tagsTypes } from "@pineseed/spec";
import { useBaseEntityAdmin, type EntityAdminHandler } from "./useEntityAdmin";
import type { UseAdminAppReturn } from "./useAdminApp";
import { useEntitySSE } from "./useEntitySSE";

type Tag = tagsTypes["Tag"];
type TagAssignment = tagsTypes["TagAssignment"];

export interface TaggerAdminHandler extends EntityAdminHandler<Tag> {
  // Tag-specific state
  assignments: TagAssignment[];
  assignmentsLoading: boolean;
  
  // Tag search with filters
  searchTags: (query: string, filters?: {
    namespace?: string;
    type?: Tag['type'];
    caseSensitive?: boolean;
  }) => Promise<void>;
  
  // Tag assignments
  loadAssignments: () => Promise<void>;
  assignTag: (data: {
    tagId: string;
    entityType: TagAssignment['entityType'];
    entityId: string;
  }) => Promise<void>;
  unassignTag: (assignment: TagAssignment) => Promise<void>;
  
  // Entity-Tag relationships
  getEntityTags: (entityType: TagAssignment['entityType'], entityId: string) => Promise<Tag[]>;
  getEntitiesWithTag: (tagId: string) => Promise<Array<{ entityType: TagAssignment['entityType']; entityId: string }>>;
  
  // Filters
  filterByNamespace: (namespace: string) => Promise<void>;
  filterByType: (type: Tag['type']) => Promise<void>;
  clearFilters: () => Promise<void>;
  
  // Statistics
  getStats: () => {
    total: number;
    byType: Record<string, number>;
    byNamespace: Record<string, number>;
    assignmentsCount: number;
  };
  
  // SSE subscriptions
  sseEnabled: boolean;
  setSseEnabled: (enabled: boolean) => void;
  includeInitialData: boolean;
  setIncludeInitialData: (include: boolean) => void;
}

export function useTaggerAdmin(adminApp: UseAdminAppReturn): TaggerAdminHandler {
  // Base CRUD operations
  const baseHandler = useBaseEntityAdmin<Tag>({
    adminApp,
    endpoint: 'tags',
    entityName: 'tag',
  });

  // Tag assignments state
  const [assignments, setAssignments] = useState<TagAssignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  // Current filters
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});

  // Load tag assignments
  const loadAssignments = useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/tags/assignments?workspaceId=${adminApp.workspaceId}`,
        {
          headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
          credentials: 'include',
        }
      );
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(`❌ API Error [GET /tags/assignments]:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });
        throw new Error('Failed to load assignments');
      }
      const data = await response.json();
      setAssignments(data.assignments || []);
      console.log('✅ Loaded tag assignments:', data.assignments?.length);
    } catch (error) {
      console.error('Failed to load assignments:', error);
      setAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [adminApp]);

  // Search tags with filters
  const searchTags = useCallback(async (
    query: string,
    filters?: {
      namespace?: string;
      type?: Tag['type'];
      caseSensitive?: boolean;
    }
  ) => {
    if (!query.trim()) {
      // If empty query, just load with filters
      await baseHandler.loadEntities(currentFilters);
      return;
    }

    baseHandler.setLoading(true);
    baseHandler.setError(null);
    
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/tags/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify({
          workspaceId: adminApp.workspaceId,
          query,
          matchFields: 'name,description',
          caseSensitive: filters?.caseSensitive || false,
          namespace: filters?.namespace,
          type: filters?.type,
          limit: 100,
          offset: 0,
        }),
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(`❌ API Error [POST /tags/search]:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });
        throw new Error('Search failed');
      }
      const data = await response.json();
      baseHandler.setEntities(data.tags || []);
      console.log('✅ Search results:', data.tags?.length);
    } catch (error) {
      const errorMsg = 'Failed to search tags';
      console.error(errorMsg, error);
      baseHandler.setError(errorMsg);
    } finally {
      baseHandler.setLoading(false);
    }
  }, [adminApp, currentFilters, baseHandler]);

  // Assign tag to entity
  const assignTag = useCallback(async (data: {
    tagId: string;
    entityType: TagAssignment['entityType'];
    entityId: string;
  }) => {
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/tags/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify({
          tagId: data.tagId,
          entityType: data.entityType,
          entityId: data.entityId,
          workspaceId: adminApp.workspaceId,
          assignedBy: 'admin',
        }),
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(`❌ API Error [POST /tags/assign]:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });
        throw new Error('Assignment failed');
      }
      console.log('✅ Tag assigned:', data.tagId, 'to', data.entityType, data.entityId);
      await loadAssignments();
    } catch (error) {
      console.error('Assign tag error:', error);
      throw error;
    }
  }, [adminApp, loadAssignments]);

  // Unassign tag from entity
  const unassignTag = useCallback(async (assignment: TagAssignment) => {
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/tags/assign?tagId=${assignment.tagId}&entityType=${assignment.entityType}&entityId=${assignment.entityId}&workspaceId=${adminApp.workspaceId}`,
        {
          method: 'DELETE',
          headers: { 'X-Workspace-Id': adminApp.workspaceId },
          credentials: 'include',
        }
      );
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(`❌ API Error [DELETE /tags/assign]:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });
        throw new Error('Unassignment failed');
      }
      console.log('✅ Tag unassigned:', assignment.tagId, 'from', assignment.entityType, assignment.entityId);
      await loadAssignments();
    } catch (error) {
      console.error('Unassign tag error:', error);
      throw error;
    }
  }, [adminApp, loadAssignments]);

  // Filter by namespace
  const filterByNamespace = useCallback(async (namespace: string) => {
    const filters = { ...currentFilters };
    if (namespace) {
      filters.namespace = namespace;
    } else {
      delete filters.namespace;
    }
    setCurrentFilters(filters);
    await baseHandler.loadEntities(filters);
  }, [currentFilters, baseHandler]);

  // Filter by type
  const filterByType = useCallback(async (type: Tag['type']) => {
    const filters = { ...currentFilters };
    if (type) {
      filters.type = type;
    } else {
      delete filters.type;
    }
    setCurrentFilters(filters);
    await baseHandler.loadEntities(filters);
  }, [currentFilters, baseHandler]);

  // Clear all filters
  const clearFilters = useCallback(async () => {
    setCurrentFilters({});
    await baseHandler.loadEntities();
  }, [baseHandler]);

  // Get tags for a specific entity
  const getEntityTags = useCallback(async (
    entityType: TagAssignment['entityType'],
    entityId: string
  ): Promise<Tag[]> => {
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/tags/entity/${entityType}/${entityId}`,
        {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(`❌ API Error [GET /tags/entity/${entityType}/${entityId}]:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });
        throw new Error('Failed to get entity tags');
      }
      const data = await response.json();
      console.log('✅ Entity tags:', data.tags?.length);
      return data.tags || [];
    } catch (error) {
      console.error('Failed to get entity tags:', error);
      throw error;
    }
  }, [adminApp]);

  // Get all entities that have a specific tag
  const getEntitiesWithTag = useCallback(async (tagId: string): Promise<Array<{ entityType: TagAssignment['entityType']; entityId: string }>> => {
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/tags/entities/${tagId}`,
        {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(`❌ API Error [GET /tags/entities/${tagId}]:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });
        throw new Error('Failed to get entities with tag');
      }
      const data = await response.json();
      console.log('✅ Entities with tag:', data.entities?.length);
      return data.entities || [];
    } catch (error) {
      console.error('Failed to get entities with tag:', error);
      throw error;
    }
  }, [adminApp]);

  // Get statistics
  const getStats = useCallback(() => {
    const byType = baseHandler.entities.reduce((acc, tag) => {
      acc[tag.type] = (acc[tag.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byNamespace = baseHandler.entities.reduce((acc, tag) => {
      acc[tag.namespace] = (acc[tag.namespace] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: baseHandler.entities.length,
      byType,
      byNamespace,
      assignmentsCount: assignments.length,
    };
  }, [baseHandler.entities, assignments]);

  // Reload assignments when workspace changes
  useEffect(() => {
    loadAssignments();
  }, [adminApp.workspaceId]); // Only depend on workspaceId

  // SSE subscription for tag CRUD events
  const tagSSE = useEntitySSE<Tag, { type: string; action: string; tag?: Tag; tagId?: string }>({
    adminApp,
    streamPath: 'tags',
    onConnected: useCallback((data: { type: 'connected'; [key: string]: unknown }) => {
      if (data.tags) {
        console.log('[Tagger SSE] Setting initial tags:', (data.tags as Tag[]).length);
        baseHandler.setEntities(data.tags as Tag[]);
      }
    }, [baseHandler]),
    onEvent: useCallback((event: { type: string; action: string; tag?: Tag; tagId?: string }) => {
      if (event.type === 'tag') {
        if (event.action === 'created' && event.tag) {
          console.log('[Tagger SSE] Tag created:', event.tag);
          baseHandler.setEntities((prev: Tag[]) => [...prev, event.tag!]);
        } else if (event.action === 'updated' && event.tag) {
          console.log('[Tagger SSE] Tag updated:', event.tag);
          baseHandler.setEntities((prev: Tag[]) => 
            prev.map((t: Tag) => t.id === event.tag!.id ? event.tag! : t)
          );
        } else if (event.action === 'deleted' && event.tagId) {
          console.log('[Tagger SSE] Tag deleted:', event.tagId);
          baseHandler.setEntities((prev: Tag[]) => 
            prev.filter((t: Tag) => t.id !== event.tagId)
          );
        }
      }
    }, [baseHandler]),
  });

  // SSE subscription for tag assignment events
  const assignmentSSE = useEntitySSE<TagAssignment, { type: string; action: string; assignment: TagAssignment }>({
    adminApp,
    streamPath: 'tags/assignments',
    onConnected: useCallback((data: { type: 'connected'; [key: string]: unknown }) => {
      if (data.assignments) {
        console.log('[Tagger SSE] Setting initial assignments:', (data.assignments as TagAssignment[]).length);
        setAssignments(data.assignments as TagAssignment[]);
      }
    }, []),
    onEvent: useCallback((event: { type: string; action: string; assignment: TagAssignment }) => {
      if (event.type === 'assignment') {
        if (event.action === 'assigned') {
          console.log('[Tagger SSE] Tag assigned:', event.assignment);
          setAssignments((prev: TagAssignment[]) => [...prev, event.assignment]);
        } else if (event.action === 'unassigned') {
          console.log('[Tagger SSE] Tag unassigned:', event.assignment);
          setAssignments((prev: TagAssignment[]) => 
            prev.filter((a: TagAssignment) => 
              !(a.tagId === event.assignment.tagId && 
                a.entityType === event.assignment.entityType && 
                a.entityId === event.assignment.entityId)
            )
          );
        }
      }
    }, []),
  });

  return {
    // Base operations
    ...baseHandler,
    
    // Tag-specific state
    assignments,
    assignmentsLoading,
    
    // Tag-specific operations
    searchTags,
    loadAssignments,
    assignTag,
    unassignTag,
    getEntityTags,
    getEntitiesWithTag,
    filterByNamespace,
    filterByType,
    clearFilters,
    getStats,
    
    // SSE subscriptions (unified control for both streams)
    sseEnabled: tagSSE.enabled,
    setSseEnabled: (enabled: boolean) => {
      tagSSE.setEnabled(enabled);
      assignmentSSE.setEnabled(enabled);
    },
    includeInitialData: tagSSE.includeInitial,
    setIncludeInitialData: (include: boolean) => {
      tagSSE.setIncludeInitial(include);
      assignmentSSE.setIncludeInitial(include);
    },
    
    // Override searchEntities to use tag search
    searchEntities: searchTags,
  };
}

